import { useState, type ReactNode } from 'react';
import { Drawer, Input, Select } from 'antd';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import '@/pages/Settings/AiAgents/AgentsTable.css';
import { startTestRun } from '@/apis/logiblocksAPI';
import { CloseDrawerIcon, StarIcon } from '@/assets/icons/icons';
import { useToken } from '@/common/helper';
import { ToggleSwitch } from '@/components';
import { usePageScrollLock } from '@/hooks/usePageScrollLock';
import customizationStyles from '../nodes/NodeCustomization.module.css';
import sidePanelStyles from '../SidePanel.module.css';
import type { ReactFlowEdge, ReactFlowNode } from '../types';
import { useSelectTestShipmentModal } from './SelectTestShipmentModal';
import styles from './TestRunPanel.module.css';
import {
  buildTestRunData,
  getAlertTriggerAlertTypes,
  getTestRunUnsupportedNodeLabels,
} from './testRunUtils';

const PANEL_WIDTH = 444;
const PATH_LET_DATA_DECIDE = 'let_data_decide';
const SKIP_DELAYS_TOGGLE_ID = 'test-run-skip-delays';
// Joins node labels as "A, B, and C".
const UNSUPPORTED_NODE_LIST_FORMAT = new Intl.ListFormat('en', {
  style: 'long',
  type: 'conjunction',
});

export interface UseTestRunPanelArgs {
  workflowId: string;
  /** The canvas graph the test run runs. */
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
  /** Called with the new run's WorkflowRunHistory id once the test run starts. */
  onStarted: (runId: string) => void;
}

export interface TestRunPanel {
  openTestRunPanel: () => void;
  testRunPanel: ReactNode;
}

/**
 * Returns the "Test automation" side panel and a function that opens it. Run starts a test run of
 * the canvas graph. Fields for inputs the test run engine does not read yet are shown disabled.
 */
export function useTestRunPanel({
  workflowId,
  nodes,
  edges,
  onStarted,
}: UseTestRunPanelArgs): TestRunPanel {
  const { getToken } = useToken();
  const [isOpen, setIsOpen] = useState(false);
  const [shipmentId, setShipmentId] = useState('');
  const [alertId, setAlertId] = useState('');
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [skipDelays, setSkipDelays] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  // The Drawer runs without antd's mask, which also skips antd's page scroll lock.
  usePageScrollLock(isOpen);

  // The start endpoint refuses a workflow containing these node types, so Run stays disabled.
  const unsupportedNodeLabels = getTestRunUnsupportedNodeLabels(nodes);
  // Only an alert trigger can pick a past shipment, so Select shipment needs its alert types.
  const alertTriggerAlertTypes = getAlertTriggerAlertTypes(nodes, edges);
  const { openSelectShipmentModal, selectShipmentModal } =
    useSelectTestShipmentModal({
      onSelect: (row) => {
        setShipmentId(row.shipment_id);
        setAlertId(row.alert_id);
      },
    });

  /**
   * Closes the panel and clears the last start error. The entered values stay for the next test.
   */
  function closeTestRunPanel(): void {
    setIsOpen(false);
    setStartError(null);
  }

  /** Starts a test run of the canvas graph, then closes the panel and reports the new run. */
  async function handleRun(): Promise<void> {
    const token = getToken();
    if (token === null) {
      console.error('Cannot start a test run: no auth token');
      setStartError('You are signed out. Sign in and try again.');
      return;
    }
    setIsStarting(true);
    setStartError(null);
    try {
      const runId = await startTestRun(
        token,
        workflowId,
        buildTestRunData(
          nodes,
          edges,
          shipmentId,
          alertId,
          emailRecipients,
          skipDelays
        )
      );
      closeTestRunPanel();
      onStarted(runId);
    } catch (error) {
      console.error('Failed to start test run:', error);
      setStartError(error instanceof Error ? error.message : String(error));
    } finally {
      setIsStarting(false);
    }
  }

  const testRunDrawer = (
    <Drawer
      title={
        <div className={sidePanelStyles.closeButtonRow}>
          <button
            type='button'
            onClick={closeTestRunPanel}
            className={sidePanelStyles.closeButton}
            aria-label='Close'
          >
            <CloseDrawerIcon />
          </button>
        </div>
      }
      placement='right'
      width={PANEL_WIDTH}
      open={isOpen}
      onClose={closeTestRunPanel}
      closable={false}
      mask={false}
      className={sidePanelStyles.drawer}
      styles={{
        body: { padding: 0 },
        header: {
          border: 'none',
          borderBottom: 'none',
          padding: '16px 16px 0px',
        },
        footer: { border: 'none', borderTop: 'none', padding: 24 },
      }}
      footer={
        <div className={sidePanelStyles.buttonDock}>
          {unsupportedNodeLabels.length > 0 && (
            <div className={styles.startError} role='alert'>
              {`${UNSUPPORTED_NODE_LIST_FORMAT.format(unsupportedNodeLabels)} nodes can't run in a test yet.`}
            </div>
          )}
          {startError !== null && (
            <div className={styles.startError} role='alert'>
              {startError}
            </div>
          )}
          <button
            type='button'
            onClick={() => {
              const _ = handleRun();
            }}
            className={sidePanelStyles.primaryFooterButton}
            disabled={isStarting || unsupportedNodeLabels.length > 0}
          >
            Run
          </button>
          <button
            type='button'
            onClick={closeTestRunPanel}
            className={sidePanelStyles.cancelFooterButton}
          >
            Cancel
          </button>
        </div>
      }
      data-testid='test-run-panel'
    >
      <div className={styles.content}>
        <div className={styles.titleBlock}>
          <span className={styles.title}>Test automation</span>
          <span className={styles.subtitle}>
            Enter a scenario to test this automation
          </span>
        </div>
        <div className={sidePanelStyles.contentDivider} />
        <div className={styles.form}>
          <div>
            <label className={customizationStyles.sectionLabel}>
              Test with
            </label>
            <div className={styles.testWithCard}>
              <button
                type='button'
                className={styles.testWithChoice}
                disabled={alertTriggerAlertTypes === null}
                onClick={() => {
                  if (alertTriggerAlertTypes !== null) {
                    openSelectShipmentModal(alertTriggerAlertTypes);
                  }
                }}
              >
                Select shipment
              </button>
              <span>or</span>
              <button type='button' className={styles.testWithChoice} disabled>
                Describe a scenario
                <StarIcon width={16} height={16} color='currentColor' />
              </button>
            </div>
            {/* TODO: Delete the Shipment ID and Alert ID inputs when the Select shipment modal and its alert picker replace them. */}
            <div className={styles.idInputs}>
              <Input
                className='agentInput'
                placeholder='Shipment ID'
                aria-label='Shipment ID'
                value={shipmentId}
                onChange={(e) => setShipmentId(e.target.value)}
              />
              <Input
                className='agentInput'
                placeholder='Alert ID'
                aria-label='Alert ID'
                value={alertId}
                onChange={(e) => setAlertId(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className={customizationStyles.sectionLabel}>Path</label>
            <Select
              className='agentSelect'
              aria-label='Path'
              value={PATH_LET_DATA_DECIDE}
              options={[
                { value: PATH_LET_DATA_DECIDE, label: 'Let data decide' },
              ]}
              disabled
            />
          </div>
          <div>
            <label className={customizationStyles.sectionLabel}>
              Email recipients
            </label>
            <Select
              className='agentSelect'
              aria-label='Email recipients'
              mode='tags'
              placeholder='Leave empty to email yourself'
              tokenSeparators={[',', ' ']}
              value={emailRecipients}
              onChange={setEmailRecipients}
            />
          </div>
          <div>
            <label className={customizationStyles.sectionLabel}>
              Call recipient
            </label>
            <PhoneInput country='us' value='' disabled />
          </div>
          <div className={styles.skipDelaysRow}>
            <ToggleSwitch
              id={SKIP_DELAYS_TOGGLE_ID}
              checked={skipDelays}
              onChange={(event) => setSkipDelays(event.target.checked)}
              alignLeft
            />
            <label
              htmlFor={SKIP_DELAYS_TOGGLE_ID}
              className={styles.skipDelaysLabel}
            >
              Skip delays
            </label>
          </div>
        </div>
      </div>
    </Drawer>
  );

  // The modal renders beside the Drawer, not inside it. Inside, its backdrop leaves the Drawer
  // undimmed.
  const testRunPanel = (
    <>
      {testRunDrawer}
      {selectShipmentModal}
    </>
  );

  return { openTestRunPanel: () => setIsOpen(true), testRunPanel };
}
